<template>
  <div class="orbital-loader" role="status" :aria-label="label || 'Loading'">
    <div class="orbital-loader__spinner">
      <div class="orbital-loader__orbit orbital-loader__orbit--1"><span class="orbital-loader__dot"></span></div>
      <div class="orbital-loader__orbit orbital-loader__orbit--2"><span class="orbital-loader__dot"></span></div>
      <div class="orbital-loader__orbit orbital-loader__orbit--3"><span class="orbital-loader__dot"></span></div>
      <div class="orbital-loader__logo">
        <LogoTile v-if="branding" :branding="branding" :size="72" :alt="logoAlt" />
      </div>
    </div>
    <p v-if="label" class="orbital-loader__label">{{ label }}</p>
  </div>
</template>

<script lang="ts">
/**
 * The MetaShip root shell's orbital boot loader (root/index.html
 * `#loading-container`, per Jarryd 2026-08-27: the tenant shell's loading
 * states should match it), re-cut for tenant apps:
 *
 * - Ring/dot/glow colours derive from the TENANT primary via
 *   `--tenant-primary` (guarded to #rrggbb by applyBranding()) through
 *   color-mix — never the hardcoded MetaShip blues.
 * - The centre mark is the tenant's logo INSIDE the white rounded-square
 *   LogoTile (house rule). The root draws its white cube raw on dark chrome;
 *   a white-label shell must never flash the MetaShip mark, and a tenant mark
 *   never sits raw on a dark surface.
 * - Scaled to 240px (the root's 340px is a full-page boot splash; this one
 *   also serves inline states like the sign-in callback).
 *
 * The pre-resolve splash in standalone/index.html carries the same geometry
 * in neutral tones with NO mark — the tenant isn't known yet.
 */
import { computed, defineComponent } from "vue";
import LogoTile from "@/shell/LogoTile.vue";
import { useSessionStore } from "@/stores/session";

export default defineComponent({
  name: "OrbitalLoader",
  components: { LogoTile },
  props: {
    label: { type: String, default: "" },
  },
  setup() {
    const session = useSessionStore();
    const branding = computed(() => session.tenant?.branding ?? null);
    const logoAlt = computed(() => session.tenant?.name ?? "Loading");
    return { branding, logoAlt };
  },
});
</script>

<style scoped>
.orbital-loader {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
}

.orbital-loader__spinner {
  position: relative;
  width: 240px;
  height: 240px;
}

.orbital-loader__orbit {
  position: absolute;
  top: 50%;
  left: 50%;
  border-radius: 50%;
  border: 2px solid transparent;
}

.orbital-loader__orbit--1 {
  width: 224px;
  height: 224px;
  margin: -112px 0 0 -112px;
  border-top-color: color-mix(in srgb, var(--tenant-primary, #649ece) 90%, transparent);
  border-right-color: color-mix(in srgb, var(--tenant-primary, #649ece) 30%, transparent);
  animation: standalone-orbit-spin 2s linear infinite;
}

.orbital-loader__orbit--2 {
  width: 180px;
  height: 180px;
  margin: -90px 0 0 -90px;
  border-bottom-color: color-mix(in srgb, var(--tenant-secondary, #316b9b) 90%, transparent);
  border-left-color: color-mix(in srgb, var(--tenant-secondary, #316b9b) 30%, transparent);
  animation: standalone-orbit-spin 2.5s linear infinite reverse;
}

.orbital-loader__orbit--3 {
  width: 138px;
  height: 138px;
  margin: -69px 0 0 -69px;
  border-top-color: color-mix(in srgb, var(--tenant-primary, #649ece) 70%, transparent);
  border-right-color: color-mix(in srgb, var(--tenant-primary, #649ece) 20%, transparent);
  animation: standalone-orbit-spin 3s linear infinite;
}

.orbital-loader__dot {
  position: absolute;
  width: 8px;
  height: 8px;
  background: var(--tenant-primary, #649ece);
  border-radius: 50%;
  box-shadow: 0 0 12px color-mix(in srgb, var(--tenant-primary, #649ece) 90%, transparent);
}

.orbital-loader__orbit--1 .orbital-loader__dot {
  top: -4px;
  left: 50%;
  margin-left: -4px;
}
.orbital-loader__orbit--2 .orbital-loader__dot {
  bottom: -4px;
  left: 50%;
  margin-left: -4px;
}
.orbital-loader__orbit--3 .orbital-loader__dot {
  top: 50%;
  right: -4px;
  margin-top: -4px;
}

.orbital-loader__logo {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
  isolation: isolate;
  animation: standalone-orbit-logo-pulse 2s ease-in-out infinite;
}

.orbital-loader__logo::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  width: 90px;
  height: 90px;
  transform: translate(-50%, -50%);
  background: radial-gradient(
    circle,
    color-mix(in srgb, var(--tenant-primary, #649ece) 45%, transparent) 0%,
    color-mix(in srgb, var(--tenant-primary, #649ece) 15%, transparent) 40%,
    transparent 70%
  );
  border-radius: 50%;
  z-index: -1;
  animation: standalone-orbit-glow-pulse 2s ease-in-out infinite;
}

.orbital-loader__label {
  margin: 0;
  font-size: 13px;
  letter-spacing: 0.02em;
  color: color-mix(in srgb, currentColor 72%, transparent);
}

@keyframes standalone-orbit-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes standalone-orbit-logo-pulse {
  0%,
  100% {
    transform: translate(-50%, -50%) scale(1);
  }
  50% {
    transform: translate(-50%, -50%) scale(1.05);
  }
}

@keyframes standalone-orbit-glow-pulse {
  0%,
  100% {
    opacity: 0.6;
    transform: translate(-50%, -50%) scale(1);
  }
  50% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1.3);
  }
}
</style>
